import { useEffect, useRef, useState } from "react";
import api, { extractErr } from "../lib/api";
import { Plus, X, FileText, Upload, Trash2, BookOpen, Edit3, Sparkles, Save, MessageSquare, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function KnowledgeBase() {
  const [courses, setCourses] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/courses").then(r => setCourses(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this course?")) return;
    await api.delete(`/courses/${id}`);
    toast.success("Course deleted");
    load();
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-700 font-semibold">AI Knowledge Base</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Courses</h1>
          <p className="text-slate-500 mt-1 text-sm">The AI uses these to draft accurate WhatsApp replies. Keep them up-to-date.</p>
        </div>
        <button
          data-testid="add-course-btn"
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-slate-950 text-white text-sm font-semibold rounded-sm hover:bg-emerald-700 flex items-center gap-1.5"
        >
          <Plus size={15} /> New course
        </button>
      </div>

      <LiveAINotes />
      <WATemplateManager />

      <div className="mb-3 mt-8">
        <h2 className="font-display text-xl font-black tracking-tight">Courses</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map(c => (
          <CourseCard key={c.id} course={c} onEdit={() => setEditing(c)} onDelete={() => remove(c.id)} onChanged={load} />
        ))}
        {courses.length === 0 && (
          <div className="col-span-full text-center py-16 border border-dashed border-slate-300 rounded-sm">
            <BookOpen size={30} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500">No courses yet. Add your first course.</div>
          </div>
        )}
      </div>

      {showAdd && <CourseModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <CourseModal course={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function WATemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get("/whatsapp/templates").then(r => setTemplates(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("确定删除这个模板？")) return;
    try { await api.delete(`/whatsapp/templates/${id}`); toast.success("模板已删除"); load(); }
    catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <div className="mt-8 border border-slate-200 rounded-sm bg-white">
      <div className="p-4 md:p-5 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-purple-700 font-semibold flex items-center gap-1.5">
            <MessageSquare size={11} /> WhatsApp Bulk Templates
          </div>
          <div className="font-display text-xl font-black tracking-tight mt-0.5">模板管理</div>
          <div className="text-xs text-slate-500 mt-1">
            🚨 <b>重要</b>：这里编辑的模板不会自动送去 Meta 审批。真正合规群发需要您先在 Meta Business Suite 提交 → Meta 审批 → 再在这里用同一个 name 存起来使用。
          </div>
        </div>
        <button
          data-testid="add-template-btn"
          onClick={() => setShowNew(true)}
          className="px-3 py-2 bg-slate-950 text-white text-sm font-semibold rounded-sm hover:bg-emerald-700 flex items-center gap-1.5"
        >
          <Plus size={14} /> New template
        </button>
      </div>
      <div className="p-4 md:p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        {templates.map(t => (
          <div key={t.id} data-testid={`tpl-card-${t.name}`} className="border border-slate-200 rounded-sm p-3 hover:border-emerald-700">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm font-bold text-slate-800 truncate">{t.name}</div>
                <div className="flex items-center gap-1 mt-1 flex-wrap">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${t.category === "MARKETING" ? "bg-orange-100 text-orange-800" : "bg-blue-100 text-blue-800"}`}>{t.category}</span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${t.status?.includes("APPROVED") ? "bg-emerald-100 text-emerald-800" : t.status === "DRAFT" ? "bg-slate-100 text-slate-700" : "bg-red-100 text-red-800"}`}>{t.status || "DRAFT"}</span>
                  <span className="text-[9px] text-slate-500">{t.language}</span>
                  {t.variables > 0 && <span className="text-[9px] text-purple-700 font-semibold">{t.variables} 变量</span>}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button data-testid={`tpl-edit-${t.name}`} onClick={() => setEditing(t)} className="p-1.5 border border-slate-200 hover:border-emerald-700 hover:text-emerald-700 rounded-sm"><Edit3 size={13} /></button>
                <button data-testid={`tpl-delete-${t.name}`} onClick={() => remove(t.id)} className="p-1.5 border border-slate-200 hover:border-red-500 hover:text-red-600 rounded-sm"><Trash2 size={13} /></button>
              </div>
            </div>
            <div className="text-xs text-slate-700 bg-slate-50 p-2 rounded-sm whitespace-pre-wrap break-words">{t.body}</div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="col-span-full text-center py-8 text-sm text-slate-500 border border-dashed border-slate-300 rounded-sm">
            <AlertCircle size={22} className="mx-auto mb-2 text-slate-300" /> 还没有模板,点右上角 <b>New template</b> 添加
          </div>
        )}
      </div>

      {showNew && <TemplateModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <TemplateModal template={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TemplateModal({ template, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: template?.name || "",
    language: template?.language || "zh_CN",
    category: template?.category || "MARKETING",
    body: template?.body || "",
    status: template?.status || "DRAFT",
  });
  const isEdit = !!template;

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await api.patch(`/whatsapp/templates/${template.id}`, form);
        toast.success("模板已更新");
      } else {
        await api.post("/whatsapp/templates", form);
        toast.success("模板已创建");
      }
      onSaved();
    } catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-sm w-full max-w-lg shadow-xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-purple-700 font-semibold">Template</div>
            <div className="font-display text-lg font-bold">{isEdit ? "编辑模板" : "新建模板"}</div>
          </div>
          <button onClick={onClose} data-testid="tpl-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Name (Meta 审批的 template name)</label>
            <input data-testid="tpl-name" required disabled={isEdit} value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="edurise_welcome_zh" className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 font-mono disabled:bg-slate-50 disabled:text-slate-500" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Category</label>
              <select data-testid="tpl-category" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="w-full mt-1 px-2 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700">
                <option>MARKETING</option>
                <option>UTILITY</option>
                <option>AUTHENTICATION</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Language</label>
              <select data-testid="tpl-language" value={form.language} onChange={(e) => setForm({...form, language: e.target.value})} className="w-full mt-1 px-2 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700">
                <option value="zh_CN">zh_CN 简中</option>
                <option value="en">en 英文</option>
                <option value="ms">ms 马来</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Status</label>
              <select data-testid="tpl-status" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full mt-1 px-2 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700">
                <option>DRAFT</option>
                <option>PENDING</option>
                <option>APPROVED</option>
                <option value="APPROVED (MOCK)">APPROVED (MOCK)</option>
                <option>REJECTED</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Body · 用 {"{{1}}"} {"{{2}}"} 表示变量</label>
            <textarea data-testid="tpl-body" required rows={5} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} placeholder="{{1}} 老师您好！特别推荐 EduRISE {{2}} 课程..." className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 resize-none whitespace-pre-wrap" />
            <div className="text-[10px] text-slate-500 mt-1">Bulk send 时会自动把 {"{{1}}"} 替换成客户 name,其他变量根据 mapping 替换</div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">取消</button>
            <button data-testid="tpl-submit" type="submit" className="px-4 py-2 bg-emerald-700 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800 flex items-center gap-1.5">
              <Save size={13} /> {isEdit ? "保存" : "创建"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LiveAINotes() {
  const [content, setContent] = useState("");
  const [initial, setInitial] = useState("");
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  useEffect(() => {
    api.get("/notes").then(r => {
      setContent(r.data.content || "");
      setInitial(r.data.content || "");
      setMeta({ updated_at: r.data.updated_at, updated_by: r.data.updated_by });
    });
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/notes", { content });
      setInitial(content);
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      toast.success("已保存 · AI 现在会引用最新内容");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };

  const dirty = content !== initial;

  return (
    <div className="mb-6 bg-white border-2 border-orange-300 rounded-sm shadow-brutal-accent" data-testid="live-ai-notes">
      <div className="p-4 border-b border-orange-200 bg-orange-50 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Sparkles size={16} className="text-orange-600" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-orange-700 font-bold">📌 Live AI Notes</div>
            <div className="font-display text-base font-bold text-orange-900">实时业务经验与本月促销</div>
          </div>
        </div>
        <div className="text-[10px] text-orange-700">
          {meta.updated_at ? `更新于 ${new Date(meta.updated_at).toLocaleString()} by ${meta.updated_by || "?"}` : "未更新"}
        </div>
      </div>
      <div className="p-4">
        <textarea
          data-testid="notes-textarea"
          value={content}
          onChange={(e) => setContent(e.target.value.slice(0, 5000))}
          rows={6}
          maxLength={5000}
          placeholder="在这里写下最新的促销、案例或话术。AI 起草回复时会最优先参考这段内容。
例如:
- 本月促销:注册 TUP Bundle 送一堂 SEL 单课
- 常见问答:XXX
- 讲师最新档期"
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 resize-y font-mono"
        />
        <div className="flex items-center justify-between mt-3">
          <div className={`text-[10px] ${content.length > 4500 ? "text-red-600 font-semibold" : "text-slate-500"}`} data-testid="notes-charcount">
            {content.length} / 5000 字 · {dirty ? "有未保存改动" : "已同步"}
          </div>
          <button
            data-testid="notes-save"
            onClick={save}
            disabled={busy || !dirty}
            className="px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-sm hover:bg-orange-600 disabled:opacity-40 flex items-center gap-1.5"
          >
            <Save size={13} /> {busy ? "Saving…" : "保存更新 Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CourseCard({ course, onEdit, onDelete, onChanged }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      await api.post(`/courses/${course.id}/brochure`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Brochure uploaded");
      onChanged();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setUploading(false); }
  };

  return (
    <div data-testid={`course-card-${course.id}`} className="bg-white border border-slate-200 rounded-sm p-5 flex flex-col hover:-translate-y-[1px] hover:shadow-sm transition-transform">
      {course.category && <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-2">{course.category}</div>}
      <div className="font-display text-lg font-bold leading-tight mb-2 tracking-tight">{course.title}</div>
      <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 mb-4">{course.description}</p>

      <div className="grid grid-cols-2 gap-3 mb-4 pt-4 border-t border-slate-100">
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-slate-400">Price</div>
          <div className="text-sm font-semibold mt-0.5">{course.price || "—"}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-slate-400">Duration</div>
          <div className="text-sm font-semibold mt-0.5">{course.duration || "—"}</div>
        </div>
      </div>

      {course.brochure_filename ? (
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm text-xs mb-3">
          <FileText size={13} className="text-emerald-700" />
          <span className="truncate flex-1">{course.brochure_filename}</span>
        </div>
      ) : (
        <div className="text-[11px] text-slate-400 mb-3">No brochure uploaded</div>
      )}

      {course.source_url && (
        <a
          href={course.source_url}
          target="_blank"
          rel="noreferrer"
          data-testid={`course-url-${course.id}`}
          className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 hover:border-emerald-500 rounded-sm text-xs text-emerald-800 mb-3 truncate"
          title={course.source_url}
        >
          🔗 <span className="truncate font-mono">{course.source_url.replace(/^https?:\/\//, "")}</span>
        </a>
      )}

      <div className="flex items-center gap-1.5 mt-auto">
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={upload} className="hidden" data-testid={`upload-input-${course.id}`} />
        <button
          data-testid={`upload-brochure-${course.id}`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex-1 px-3 py-1.5 border border-slate-200 hover:border-emerald-700 hover:text-emerald-700 text-xs font-semibold rounded-sm flex items-center justify-center gap-1.5"
        >
          <Upload size={12} /> {uploading ? "Uploading..." : "Brochure"}
        </button>
        <button data-testid={`edit-course-${course.id}`} onClick={onEdit} className="p-1.5 border border-slate-200 hover:border-emerald-700 hover:text-emerald-700 rounded-sm">
          <Edit3 size={13} />
        </button>
        <button data-testid={`delete-course-${course.id}`} onClick={onDelete} className="p-1.5 border border-slate-200 hover:border-red-600 hover:text-red-600 rounded-sm">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function CourseModal({ course, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: course?.title || "",
    category: course?.category || "",
    description: course?.description || "",
    price: course?.price || "",
    duration: course?.duration || "",
    syllabus: course?.syllabus || "",
    source_url: course?.source_url || "",
  });
  const isEdit = !!course;
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) await api.patch(`/courses/${course.id}`, form);
      else await api.post("/courses", form);
      toast.success(isEdit ? "Course updated" : "Course created");
      onSaved();
    } catch (e) { toast.error(extractErr(e)); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-sm w-full max-w-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display text-lg font-bold">{isEdit ? "Edit course" : "New course"}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <input data-testid="course-title" required value={form.title} onChange={upd("title")} placeholder="Course title" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
          <input data-testid="course-category" value={form.category} onChange={upd("category")} placeholder="Category (e.g. AI教师技能)" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
          <textarea data-testid="course-description" required value={form.description} onChange={upd("description")} rows={3} placeholder="Description" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 resize-none" />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="course-price" value={form.price} onChange={upd("price")} placeholder="Price (RM 668)" className="px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
            <input data-testid="course-duration" value={form.duration} onChange={upd("duration")} placeholder="Duration" className="px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-1 block">🔗 报名 / 付款 URL (AI 会主动附给客户)</label>
            <input data-testid="course-source-url" value={form.source_url} onChange={upd("source_url")} placeholder="https://app.edurise.com.my/courses/..." className="w-full px-3 py-2 text-sm border-2 border-emerald-200 rounded-sm focus:outline-none focus:border-emerald-700 font-mono" />
          </div>
          <textarea data-testid="course-syllabus" value={form.syllabus} onChange={upd("syllabus")} rows={4} placeholder="Syllabus / key topics" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 resize-none" />
          <button data-testid="course-submit" type="submit" className="w-full py-2.5 bg-emerald-700 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800">
            {isEdit ? "Save changes" : "Create course"}
          </button>
        </form>
      </div>
    </div>
  );
}
